import React from "react";
import { useParams } from "react-router-dom";
import { tv } from "tailwind-variants";

import { MODAL_ROUTES, useNavigateModal } from "~/router";
import { TIMEZONES } from "~/screens/Scheduling/InitialVisit/utils/date-time-utils";
import {
  Appointment,
  VISIT_TYPES,
  VisitType,
  visitTypeLabels,
} from "~/services";
import { Button } from "~/shared";
import { dateToUsFormat } from "~/shared/utils";

const visitCardBarVariants = tv({
  base: "w-[6px] self-stretch",
  variants: {
    visitType: {
      [VISIT_TYPES.INITIAL]: "bg-lavender",
      [VISIT_TYPES.FOLLOW_UP]: "bg-blue",
      [VISIT_TYPES.COACH]: "bg-green",
      [VISIT_TYPES.NUTRITION_INITIAL_VISIT]: "bg-yellow",
      [VISIT_TYPES.NUTRITION_FOLLOW_UP_VISIT]: "bg-orange",
    },
  },
  defaultVariants: {
    visitType: VISIT_TYPES.INITIAL,
  },
});

type VisitCardProps = {
  visit: Appointment;
  showCancelButton?: boolean;
};

export const VisitCard = ({
  visit,
  showCancelButton = false,
}: VisitCardProps) => {
  const navigateModal = useNavigateModal();
  const params = useParams<{ userId: string }>();
  const providerDisplayName = visit.provider.title
    ? `${visit.provider.name}, ${visit.provider.title}`
    : visit.provider.name;

  return (
    <div
      className="flex w-full items-center gap-2 rounded-md border border-neutral-300 bg-white p-2"
      role="article"
      aria-label={`Visit scheduled with ${providerDisplayName}`}
    >
      <div
        className={visitCardBarVariants({
          visitType: visit.type as VisitType,
        })}
        aria-hidden="true"
      />

      <div className="flex flex-1 items-start gap-2">
        <div className="flex flex-1 flex-col gap-1 border-r border-neutral-300 pr-2">
          <div className="text-base font-semibold text-neutral-950">
            {providerDisplayName}
          </div>

          <div className="text-sm text-neutral-950">
            {visitTypeLabels[visit.type as VisitType]}
          </div>

          <div className="flex items-center gap-4 text-sm text-neutral-800">
            {dateToUsFormat(visit.date, TIMEZONES.ET.code)}
          </div>
        </div>

        <div className="flex flex-1 text-xs text-neutral-900">
          <span className="pr-1 font-semibold">Department:</span>
          {visit.department}
        </div>

        {showCancelButton && (
          <Button
            variant="outline"
            size="sm"
            className="border-poppyRed text-poppyRed hover:bg-poppyRed py-1 hover:text-white"
            onClick={() =>
              navigateModal(
                `${MODAL_ROUTES.cancelAVisit}/${params.userId}/${visit.id}`,
              )
            }
          >
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
};
